(() => {
  if (customElements.get('pos-discount-popup')) return;
  const REWARD_LIFETIME = 24 * 60 * 60 * 1000;

  // Only timestamps and status flags are stored; email addresses stay in the form.
  const read = (storage, key) => {
    try { return JSON.parse(window[storage].getItem(key) || 'null'); } catch (_) { return null; }
  };
  const write = (storage, key, value) => {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch (_) { /* Private browsing. */ }
  };

  class PosDiscountPopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // Editor navigation can connect the element before its children are parsed.
      if (!this.querySelector('[data-pos-copy-status]')) {
        if (!this.parseObserver) {
          this.parseObserver = new MutationObserver(() => {
            if (this.querySelector('[data-pos-copy-status]')) this.connectedCallback();
          });
          this.parseObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      this.parseObserver?.disconnect();
      this.parseObserver = null;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.designMode = this.dataset.designMode === 'true';
      this.key = `pos-popup:${this.dataset.sectionId}:${this.dataset.campaign}`;
      this.pendingKey = `pos-popup-pending:${this.dataset.sectionId}`;
      this.subscribedKey = 'pos-popup-subscribed';
      this.reward = this.querySelector('[data-pos-reward]');
      // Keep editor simulations across preview pages without marking a real signup.
      this.rewardStorage = this.designMode ? 'sessionStorage' : 'localStorage';
      this.rewardKey = `${this.key}${this.designMode ? ':preview' : ''}:reward:${this.reward?.querySelector('[data-pos-reward-code]').textContent.trim() || ''}`;
      this.rewardExpiresAt = 0;
      this.teaser = this.querySelector('[data-pos-teaser]');
      this.teaserDismissedKey = `${this.key}:teaser-hidden`;
      this.teaserDismissed = !this.designMode && Boolean(read('sessionStorage', this.teaserDismissedKey));
      this.success = false;

      this.querySelectorAll('[data-pos-open]').forEach(button => button.addEventListener('click', () => this.open(), options));
      this.querySelectorAll('[data-pos-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), options));
      this.querySelector('[data-pos-hide-teaser]')?.addEventListener('click', () => {
        this.teaserDismissed = true;
        if (!this.designMode) write('sessionStorage', this.teaserDismissedKey, true);
        this.updateTeaser();
      }, options);
      this.dialog.addEventListener('close', () => this.onClose(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);

      this.querySelector('[data-pos-preview-submit]')?.addEventListener('click', event => this.submitPreview(event), options);
      this.querySelector('[data-pos-preview-email]')?.addEventListener('keydown', event => {
        if (event.key === 'Enter') this.submitPreview(event);
      }, options);
      this.querySelector('input[type="email"]')?.addEventListener('input', () => this.clearEmailError(), options);
      const form = this.querySelector('form');
      // Show our English inline errors instead of browser-language tooltips.
      if (form) form.noValidate = true;
      form?.addEventListener('submit', event => {
        if (!this.validateEmail()) {
          event.preventDefault();
          return;
        }
        if (this.designMode) {
          event.preventDefault();
          this.showSuccess();
          return;
        }
        // Keep Shopify's native POST, validation and CAPTCHA flow intact.
        const returnInput = form.querySelector('[name="return_to"]');
        if (returnInput) {
          const returnUrl = new URL(window.location.href);
          returnUrl.searchParams.set('customer_posted', 'true');
          returnUrl.searchParams.set('pos_popup', this.dataset.sectionId);
          returnInput.value = returnUrl.pathname + returnUrl.search;
        }
        write('sessionStorage', this.pendingKey, Date.now());
      }, options);
      this.querySelector('[data-pos-copy]')?.addEventListener('click', async event => {
        const button = event.currentTarget;
        if (!await this.copyCode()) return;
        const label = button.dataset.label || button.textContent;
        button.dataset.label = label;
        button.textContent = button.dataset.copied;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => { button.textContent = label; }, 2000);
      }, options);
      this.reward?.querySelector('[data-pos-reward-copy]').addEventListener('click', async event => {
        const button = event.currentTarget;
        const copied = await this.copyCode(this.reward.querySelector('[data-pos-reward-code]'), button, this.reward.querySelector('[data-pos-reward-status]'));
        if (!copied) return;
        const label = button.dataset.label || button.textContent;
        button.dataset.label = label;
        button.textContent = button.dataset.copied;
        clearTimeout(this.rewardCopyTimer);
        this.rewardCopyTimer = setTimeout(() => { button.textContent = label; }, 2000);
      }, options);
      window.addEventListener('pageshow', event => {
        this.updateReward();
        if (event.persisted && this.isSuppressed()) {
          if (this.dialog.open) this.dialog.close();
          this.updateTeaser();
        }
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.updateReward();
          if (this.readyToOpen) this.openAutomatically();
        }
      }, options);
      window.addEventListener('storage', event => {
        if (event.key === this.rewardKey || event.key === null) this.updateReward();
      }, options);
      this.updateReward();

      if (this.designMode) {
        this.updateTeaser();
        document.addEventListener('shopify:section:select', event => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open();
        }, options);
        document.addEventListener('shopify:section:deselect', event => {
          if (event.detail.sectionId === this.dataset.sectionId && this.dialog.open) this.dialog.close();
        }, options);
        if (this.dataset.preview === 'true') this.timer = setTimeout(() => this.open(), 100);
        return;
      }

      const result = this.querySelector('[data-pos-result]');
      const url = new URL(window.location.href);
      const pendingAt = read('sessionStorage', this.pendingKey);
      const isPending = typeof pendingAt === 'number' && Date.now() - pendingAt < 30 * 60 * 1000;
      const returnedHere = url.searchParams.get('pos_popup') === this.dataset.sectionId || isPending;
      if (returnedHere && result?.dataset.success === 'true') {
        write('localStorage', this.subscribedKey, true);
        write('sessionStorage', this.pendingKey, null);
        url.searchParams.delete('customer_posted');
        url.searchParams.delete('pos_popup');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        this.showSuccess();
        this.open();
        return;
      }
      if (returnedHere && result?.dataset.errors === 'true') {
        this.querySelector('[data-pos-errors]').hidden = false;
        this.querySelector('input[type="email"]').setAttribute('aria-invalid', 'true');
        write('sessionStorage', this.pendingKey, null);
        this.open();
        return;
      }
      this.updateTeaser();
      if (this.dataset.configured !== 'true' || this.isSuppressed()) return;
      this.timer = setTimeout(() => {
        this.readyToOpen = true;
        this.openAutomatically();
      }, Math.max(0, Number(this.dataset.delay) || 0) * 1000);
    }

    submitPreview(event) {
      if (!this.designMode) return;
      event.preventDefault();
      if (this.validateEmail()) this.showSuccess();
    }

    clearEmailError() {
      this.querySelector('input[type="email"]')?.removeAttribute('aria-invalid');
      const error = this.querySelector('[data-pos-email-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
    }

    validateEmail() {
      const input = this.querySelector('input[type="email"]');
      if (!input) return false;
      this.clearEmailError();
      if (input.validity.valid) return true;
      const error = this.querySelector('[data-pos-email-error]');
      if (error) {
        error.textContent = input.validity.valueMissing
          ? 'Please enter your email address.'
          : 'Please enter a valid email address.';
        error.hidden = false;
      }
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
      return false;
    }

    isSuppressed() {
      if (read('localStorage', this.subscribedKey)) return true;
      const until = read('localStorage', this.key);
      return typeof until === 'number' && until > Date.now();
    }

    openAutomatically() {
      if (document.hidden || this.isSuppressed()) return;
      // Let an existing cart, search or consent dialog finish first.
      const hasVisibleDialog = [...document.querySelectorAll('dialog[open], [aria-modal="true"]:not([aria-hidden="true"])')]
        .some(dialog => dialog.getClientRects().length > 0 && getComputedStyle(dialog).visibility !== 'hidden');
      if (hasVisibleDialog) {
        this.timer = setTimeout(() => this.openAutomatically(), 2000);
        return;
      }
      this.readyToOpen = false;
      this.open();
    }

    open() {
      if (!this.isConnected || this.dialog.open) return;
      if (!this.designMode && this.dataset.configured !== 'true') return;
      clearTimeout(this.timer);
      this.readyToOpen = false;
      this.previousFocus = document.activeElement;
      this.dialog.showModal();
      document.documentElement.classList.add('pos-popup-open');
      if (this.teaser) this.teaser.hidden = true;
      if (this.success) this.querySelector('[data-pos-success-title]').focus({ preventScroll: true });
    }

    onClose() {
      document.documentElement.classList.remove('pos-popup-open');
      if (!this.designMode) {
        const days = Math.max(1, Number(this.dataset.frequency) || 7);
        write('localStorage', this.key, Date.now() + days * 86400000);
      }
      this.updateTeaser();
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }

    updateTeaser() {
      if (!this.teaser) return;
      const unavailable = !this.designMode && (this.dataset.configured !== 'true' || Boolean(read('localStorage', this.subscribedKey)));
      this.teaser.hidden = unavailable || this.dataset.showTeaser !== 'true' || this.teaserDismissed || this.dialog.open;
    }

    showSuccess() {
      this.success = true;
      this.activateReward();
      this.querySelector('[data-pos-signup]').hidden = true;
      const success = this.querySelector('[data-pos-success]');
      success.hidden = false;
      const title = this.querySelector('[data-pos-success-title]');
      if (!title.id) title.id = `${this.id}-success-title`;
      this.dialog.setAttribute('aria-labelledby', title.id);
      this.dialog.removeAttribute('aria-describedby');
      if (this.dialog.open) title.focus({ preventScroll: true });
    }

    activateReward() {
      if (!this.reward) return;
      const saved = read(this.rewardStorage, this.rewardKey) || this.rewardExpiresAt;
      this.rewardExpiresAt = Number.isFinite(saved) && saved > Date.now() ? saved : Date.now() + REWARD_LIFETIME;
      write(this.rewardStorage, this.rewardKey, this.rewardExpiresAt);
      this.updateReward();
    }

    updateReward() {
      if (!this.reward) return;
      clearTimeout(this.rewardTimer);
      const saved = read(this.rewardStorage, this.rewardKey);
      if (Number.isFinite(saved)) this.rewardExpiresAt = saved;
      const remaining = this.rewardExpiresAt - Date.now();
      this.reward.hidden = remaining <= 0;
      if (remaining <= 0) {
        this.rewardExpiresAt = 0;
        if (saved !== null) {
          try { window[this.rewardStorage].removeItem(this.rewardKey); } catch (_) { /* Private browsing. */ }
        }
        return;
      }
      if (this.reward.parentElement !== document.body) document.body.prepend(this.reward);
      this.rewardTimer = setTimeout(() => this.updateReward(), Math.min(remaining, REWARD_LIFETIME));
    }

    async copyCode(code = this.querySelector('[data-pos-code]'), button = this.querySelector('[data-pos-copy]'), status = this.querySelector('[data-pos-copy-status]')) {
      if (!code || !button) return false;
      const value = code.textContent.trim();
      // Copy while the click still has user activation, including embedded previews.
      const input = document.createElement('textarea');
      input.value = value;
      input.readOnly = true;
      input.tabIndex = -1;
      input.setAttribute('aria-hidden', 'true');
      input.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;';
      (this.dialog.open ? this.dialog : document.body).append(input);
      let copied = false;
      try {
        input.focus({ preventScroll: true });
        input.select();
        input.setSelectionRange(0, value.length);
        copied = document.execCommand('copy');
      } catch (_) { /* Fall back to the Clipboard API. */ }
      input.remove();
      button.focus({ preventScroll: true });
      if (copied) {
        if (status) status.textContent = button.dataset.copied;
        return true;
      }
      try {
        await navigator.clipboard.writeText(value);
        if (status) status.textContent = button.dataset.copied;
        return true;
      } catch (_) {
        const range = document.createRange();
        range.selectNodeContents(code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        if (status) status.textContent = 'Select and copy the code above.';
        return false;
      }
    }

    disconnectedCallback() {
      this.parseObserver?.disconnect();
      this.parseObserver = null;
      clearTimeout(this.timer);
      clearTimeout(this.rewardTimer);
      clearTimeout(this.rewardCopyTimer);
      clearTimeout(this.copyTimer);
      if (this.reward?.parentElement === document.body) this.prepend(this.reward);
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
      document.documentElement.classList.remove('pos-popup-open');
    }
  }
  customElements.define('pos-discount-popup', PosDiscountPopup);
})();
